/**
 * Утилиты для модалки создания/редактирования задачи.
 */

import type { KeyboardEvent } from 'react';

import { intOf } from '@/shared/utils/nullable';

/** Alt+↓ открывает закрытый попап — как у нативного <select>. Общий
 *  обработчик для всех кастомных выпадашек модалки создания задачи. */
export function onAltArrowDownOpen(isOpen: boolean, open: () => void) {
  return (e: KeyboardEvent) => {
    if (e.altKey && e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      open();
    }
  };
}

/** Нормализует массив в список положительных целых чисел.
 *  Терпим к обёртке Go { Int64, Valid } (см. TaskTemplate), не только к числам. */
export function normalizeIds(arr: unknown): number[] {
  if (!Array.isArray(arr)) {
    return [];
  }
  return arr.map((x) => intOf(x)).filter((n) => n > 0);
}

/** Приоритет → число 0…10 */
export function parsePriorityToNumber(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(10, Math.trunc(n)));
}

/** Дедлайн «через N минут» → ISO datetime-local строка */
export function deadlineAfterToISO(deadlineAfter: number): string {
  const n = Number(deadlineAfter);
  if (!Number.isFinite(n) || n <= 0) {
    return '';
  }
  const d = new Date(Date.now() + n * 60_000);
  const pad = (x: number) => String(x).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const MM = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${HH}:${MM}`;
}

/** datetime-local → UTC ISO строка */
export function datetimeLocalToUTCISOString(datetimeLocal: string): string {
  const s = String(datetimeLocal ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) {
    return '';
  }

  const yyyy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  const HH = Number(m[4]);
  const MM = Number(m[5]);
  const SS = Number(m[6] ?? '0');

  const d = new Date(yyyy, mm - 1, dd, HH, MM, SS);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  return d.toISOString();
}

/** Безопасное извлечение текста из гетерогенных объектов API */
export function uiText(v: unknown): string {
  if (v == null) {
    return '';
  }
  if (
    typeof v === 'string' ||
    typeof v === 'number' ||
    typeof v === 'boolean'
  ) {
    return String(v);
  }

  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    if ('String' in obj) {
      return String(obj.String ?? '');
    }
    if ('VALUE' in obj) {
      return String(obj.VALUE ?? '');
    }
    if ('value' in obj) {
      return String(obj.value ?? '');
    }
  }

  return '';
}

/** EntityTypeID → название продукта */
const ENTITY_TYPE_MAP: Record<number, string> = {
  1076: 'Битрикс24',
  1080: 'Сайты',
  1084: 'Модули',
  1094: 'Тех. поддержка',
  1112: 'Дизайн',
};

export function entityTypeToProduct(entityTypeId: number): string {
  return ENTITY_TYPE_MAP[entityTypeId] ?? 'Проект';
}

/** Уникальные непустые строки (сохраняя порядок) */
export function uniqStrings(arr: unknown[]): string[] {
  const out: string[] = [];
  const set = new Set<string>();
  for (const x of arr) {
    const s = String(x ?? '').trim();
    if (!s || set.has(s)) {
      continue;
    }
    set.add(s);
    out.push(s);
  }
  return out;
}

/** Проект → CRM-привязка вида "T{entityTypeId}_{id}" */
export function projectToCrmBinding(p: {
  id?: number | string | null;
  entityTypeId?: number | string | null;
}): string | null {
  const id = Number(p?.id ?? 0);
  const entityTypeId = Number(p?.entityTypeId ?? 0);
  if (
    !Number.isFinite(id) ||
    id <= 0 ||
    !Number.isFinite(entityTypeId) ||
    entityTypeId <= 0
  ) {
    return null;
  }
  return `T${entityTypeId}_${id}`;
}

/** Ограничить приоритет 0…10 */
export function clampPriority(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(10, Math.trunc(n)));
}

/** Массив id → toggle одного id. Id числовой у legacy и UUID-строка у v2. */
export function toggleId<TId extends string | number>(
  arr: TId[],
  id: TId
): TId[] {
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}
